import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  AutoComplete,
  Button,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tabs,
  Typography,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';

import TipTap from '../Compass/Sections/TipTap';
import { CorsiaMethodologyEntry, CorsiaPhase } from '../../api/corsia';
import { getProjectTypes } from '../../api/project_type';

export type CompassCorsiaRow = {
  project_type: string;
  standard?: string;
  methodology?: string;
  version?: string | string[];
  eligibility?: any;
};

type Props = {
  open: boolean;
  /** Present when editing; absent when creating. */
  initial?: { projectType: string; entry: CorsiaMethodologyEntry } | null;
  compassRows: CompassCorsiaRow[];
  defaultPhases: CorsiaPhase[];
  splitEligibility: (doc: any) => Record<string, any>;
  onCancel: () => void;
  onSave: (projectType: string, entry: CorsiaMethodologyEntry) => void;
  /** Present only for entries already saved in the payload. */
  onRemove?: () => void;
};

const uniq = (values: any[]) =>
  Array.from(new Set(values.filter(Boolean).map(String))).map((value) => ({ value }));

const EntryModal: React.FC<Props> = ({
  open,
  initial,
  compassRows,
  defaultPhases,
  splitEligibility,
  onCancel,
  onSave,
  onRemove,
}) => {
  const [projectType, setProjectType] = useState<string>('');
  const [standard, setStandard] = useState<string>('');
  const [methodology, setMethodology] = useState<string>('');
  const [versions, setVersions] = useState<string[]>([]);
  const [eligibility, setEligibility] = useState<Record<string, any>>({});

  // Reload the form whenever the modal opens for a different entry.
  useEffect(() => {
    if (!open) return;
    setProjectType(initial?.projectType ?? '');
    setStandard(initial?.entry.standard ?? '');
    setMethodology(initial?.entry.methodology ?? '');
    setVersions(initial?.entry.versions ?? []);
    setEligibility(initial?.entry.eligibility ?? {});
  }, [open, initial]);

  const { data: projectTypes } = useQuery('projectTypes', getProjectTypes);

  // Compass rows and the edited entry are folded in so saved values still resolve.
  const projectTypeOptions = useMemo(() => {
    const names = (projectTypes?.response ?? [])
      .filter((pt: any) => pt?.name?.toLowerCase() !== 'orphan')
      .map((pt: any) => pt.display_name);
    return Array.from(
      new Set([...names, ...compassRows.map((c) => c.project_type), initial?.projectType].filter(Boolean))
    )
      .sort()
      .map((value) => ({ value, label: value }));
  }, [projectTypes, compassRows, initial?.projectType]);

  const forType = useMemo(
    () => compassRows.filter((c) => !projectType || c.project_type === projectType),
    [compassRows, projectType]
  );

  const phases = initial?.entry.phases?.length ? initial.entry.phases : defaultPhases;

  /** Picking a standard pulls that Compass entry's details in as a starting point. */
  const applyCompassEntry = (nextStandard: string) => {
    setStandard(nextStandard);
    const match = forType.find((c) => c.standard === nextStandard);
    if (!match) return;
    setMethodology(match.methodology ?? '');
    setVersions(
      (Array.isArray(match.version) ? match.version : [match.version])
        .filter((v: any) => v != null && String(v).trim() !== '')
        .map((v: any) => String(v).trim())
    );
    const split = splitEligibility(match.eligibility);
    if (Object.keys(split).length) setEligibility(split);
  };

  const handleSave = () => {
    if (!projectType) return;
    onSave(projectType, {
      id: initial?.entry.id ?? uuidv4(),
      standard,
      methodology,
      versions,
      phases,
      eligibility,
    });
  };

  return (
    <Modal
      title={initial ? 'Edit entry' : 'Add entry'}
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      okButtonProps={{ disabled: !projectType }}
      width={860}
      destroyOnClose
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="flex items-center justify-between">
          <span>
            {onRemove && (
              <Popconfirm
                title="Remove this entry?"
                onConfirm={onRemove}
                okText="Remove"
                cancelText="Cancel"
              >
                <Button danger>Remove</Button>
              </Popconfirm>
            )}
          </span>
          <Space>
            <CancelBtn />
            <OkBtn />
          </Space>
        </div>
      )}
    >
      <Form layout="vertical">
        <Form.Item label="Project type" required>
          <Select
            placeholder="Select a project type"
            options={projectTypeOptions}
            value={projectType || undefined}
            onChange={(value) => {
              setProjectType(value);
              if (!initial) applyCompassEntry('');
            }}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item label="Registry / Standard">
          <AutoComplete
            placeholder={projectType ? 'Select or type a standard' : 'Choose a project type first'}
            options={uniq(forType.map((c) => c.standard))}
            value={standard}
            onChange={applyCompassEntry}
            disabled={!projectType}
            filterOption={(input, option) =>
              String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item label="Methodology">
          <AutoComplete
            placeholder="Select or type a methodology"
            options={uniq(forType.map((c) => c.methodology))}
            value={methodology}
            onChange={setMethodology}
            filterOption={(input, option) =>
              String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item label="Version(s)">
          <Select
            mode="tags"
            placeholder="Select or type version(s)"
            options={uniq(
              forType.flatMap((c) => (Array.isArray(c.version) ? c.version : [c.version]))
            )}
            value={versions}
            onChange={setVersions}
          />
        </Form.Item>

        <Typography.Text strong>Eligibility</Typography.Text>
        <Tabs
          items={phases.map((phase) => ({
            key: phase.key,
            label: eligibility?.[phase.key]?.content?.length
              ? phase.label
              : `${phase.label} · empty`,
            children: (
              <TipTap
                value={eligibility?.[phase.key]}
                onChange={(value: any) =>
                  setEligibility((prev) => ({ ...prev, [phase.key]: value }))
                }
              />
            ),
          }))}
        />
      </Form>
    </Modal>
  );
};

export default EntryModal;
